import { jsPDF } from 'jspdf';

import { IExportBrandingOptions } from '../provider/export-branding';

/**
 * Arabic letters and presentation forms — the ranges whose visual order is
 * right-to-left. Deliberately excludes digits and combining marks, which are
 * handled separately below.
 */
const ARABIC_LETTERS = /[ؠ-يٮ-ۓۺ-ۿݐ-ݿﭐ-﷿ﹰ-ﻼ]/;

/**
 * Arabic combining marks — vowel points, shadda, sukun. Each belongs to the
 * letter that precedes it and must travel with it through a reversal.
 */
const ARABIC_MARKS = /[ً-ٰٟۖ-ۭﹰ-ﹿ]/;

/**
 * Western and Arabic-Indic digits.
 *
 * @remarks
 * Arabic-Indic digits are Unicode class AN: they render *left to right* even
 * inside Arabic text. `INTL_LOCALES['ar'] = 'ar-SA'` emits them for every
 * reading in the app, so reversing one would print the number backwards.
 */
const DIGITS = /[0-9٠-٩۰-۹]/;

/**
 * Whitespace, used to split text into words.
 */
const WHITESPACE = /\s/;

/**
 * Cache of embedded fonts, keyed by URL, shared across every jsPDF document.
 * The fetch and base64 encode happen once per session, not once per export.
 */
const fontCache = new Map<string, Promise<string>>();

/**
 * True when the text contains at least one Arabic letter.
 *
 * @param text - The text to test.
 * @returns Whether the text needs right-to-left treatment.
 */
export function hasArabic(text: string): boolean {
  return ARABIC_LETTERS.test(text);
}

/**
 * Reorders one string for placement in a PDF.
 *
 * @param text - Logical-order text, as it is stored and displayed in the app.
 * @returns The same text in the visual order jsPDF will draw it.
 *
 * @remarks
 * **Shape, then reorder — in that order.** jsPDF handles *shaping* via
 * `processArabic`, registered on its `preProcessText` event, so contextual
 * initial/medial/final letter forms are applied inside `doc.text()` — and
 * `jspdf-autotable` draws every cell through `doc.text()`. What jsPDF does not
 * do is *ordering*.
 *
 * That event fires on whatever string it is handed, so reversing first makes it
 * shape reversed neighbours: every letter picks up the form of the wrong side
 * and the word renders as disconnected, back-to-front glyphs. So the shaper is
 * called here first, on logical-order text, and the reversal is applied to its
 * output. The later `preProcessText` pass over that output is a no-op: it keys
 * its substitution table on base codepoints (U+06xx) and finds nothing to do
 * with the presentation forms (U+FBxx/U+FExx) it has already produced.
 *
 * Its own `setR2L(true)` is not the answer either: it reverses the whole string
 * character by character, turning `3323.38 SAR` into `RAS 83.3233` and a meter
 * label like `ZN.4-PB.2_child__1_ch3pm` into noise. So this works at word
 * level instead — it reverses the order of the words, then reverses the
 * letters inside Arabic words only, keeping digit groups intact and keeping
 * every combining mark attached to its base letter.
 *
 * **Limitation.** A word-level approximation, not a UAX#9 bidi implementation.
 * It is correct for the shapes this app produces — an Arabic label, or an
 * Arabic phrase beside a number and a unit — and can misorder deeply nested
 * bidi (Arabic containing Latin containing Arabic). Where that matters,
 * `metadata.contentType: 'element'` routes the content through the browser's
 * own bidi engine instead.
 */
export function applyPdfBidi(text: string): string {

  if (!hasArabic(text)) {
    return text;
  }

  const shaped = shapeArabic(text);

  // Split into alternating word and whitespace tokens, so the gaps survive
  // the reversal in the right places instead of collecting at one end.
  const tokens: { isWhitespace: boolean; text: string }[] = [];

  for (const char of shaped) {
    const isWhitespace = WHITESPACE.test(char);
    const current = tokens.at(-1);

    if (current != null && current.isWhitespace === isWhitespace) {
      current.text += char;
      continue;
    }

    tokens.push({ isWhitespace, text: char });
  }

  return tokens
    .reverse()
    .map((token) => (!token.isWhitespace && hasArabic(token.text) ? reverseArabicWord(token.text) : token.text))
    .join('');
}

/**
 * Applies Arabic contextual shaping to logical-order text.
 *
 * @param text - Logical-order text.
 * @returns The same text with each letter replaced by its contextual
 *   presentation form, and lam-alef pairs resolved to their ligature.
 *
 * @remarks
 * Delegates to jsPDF's own shaper rather than reimplementing the substitution
 * tables, so the forms drawn are exactly the ones the renderer expects. It
 * lives on the `jsPDF.API` prototype, takes a string and returns a string
 * without touching `this`, so it is callable off the document.
 */
function shapeArabic(text: string): string {

  const shaper = (jsPDF.API as unknown as { processArabic?: (value: string) => string }).processArabic;

  return shaper?.(text) ?? text;
}

/**
 * Reverses one Arabic word without breaking its indivisible parts.
 *
 * @param word - The word to reverse.
 * @returns The word in visual order.
 *
 * @remarks
 * Reverses *clusters* rather than raw characters: a combining mark stays
 * behind its base letter, and a run of digits keeps its own left-to-right
 * order, so `الرقم١٢٣` does not come out as `٣٢١`.
 */
function reverseArabicWord(word: string): string {

  const clusters: string[] = [];

  for (const char of word) {
    const previous = clusters.at(-1);

    if (previous != null && ARABIC_MARKS.test(char)) {
      clusters[clusters.length - 1] = previous + char;
      continue;
    }

    if (previous != null && DIGITS.test(char) && DIGITS.test(previous.slice(-1))) {
      clusters[clusters.length - 1] = previous + char;
      continue;
    }

    clusters.push(char);
  }

  return clusters.reverse().join('');
}

/**
 * Fetches, embeds and activates the Arabic font on a document.
 *
 * @param doc - The jsPDF document to register the font on.
 * @param branding - Branding options carrying the font URLs and family name.
 * @returns The registered font family name.
 * @throws When a font cannot be fetched — an Arabic document without it
 *   renders as empty boxes, which is worse than a visible failure.
 *
 * @remarks
 * Both faces are registered, not just the regular one. jsPDF resolves a font by
 * `(family, style)` and, finding no entry, falls back to Helvetica with only a
 * console warning — so a single bold cell in an Arabic table (`jspdf-autotable`
 * bolds header rows and the field column of a transposed section) comes out as
 * Latin mojibake while the rest of the page reads correctly. Italic styles are
 * aliased onto the same files: Tajawal ships no italic face, and an alias is
 * what keeps that fallback from ever being reached.
 */
export async function loadArabicFont(doc: jsPDF, branding: IExportBrandingOptions): Promise<string> {

  const { arabicFontUrl, arabicFontBoldUrl, arabicFontName } = branding;

  const [regular, bold] = await Promise.all([
    embedFont(doc, arabicFontUrl, arabicFontName),
    embedFont(doc, arabicFontBoldUrl, arabicFontName),
  ]);

  doc.addFont(regular, arabicFontName, 'normal');
  doc.addFont(regular, arabicFontName, 'italic');
  doc.addFont(bold, arabicFontName, 'bold');
  doc.addFont(bold, arabicFontName, 'bolditalic');

  doc.setFont(arabicFontName, 'normal');

  return arabicFontName;
}

/**
 * Fetches one font file and places it in the document's virtual file system.
 *
 * @param doc - The document to add the file to.
 * @param url - The font URL.
 * @param familyName - Family name, used only for the fallback file name.
 * @returns The name the file was stored under, for `addFont`.
 * @throws When the font cannot be fetched.
 */
async function embedFont(doc: jsPDF, url: string, familyName: string): Promise<string> {

  const fileName = url.split('/').pop() ?? `${familyName}.ttf`;

  let pending = fontCache.get(url);

  if (pending == null) {
    pending = fetchAsBase64(url);
    fontCache.set(url, pending);
  }

  let base64: string;

  try {
    base64 = await pending;
  }
  catch (error: unknown) {
    // A failed fetch must not poison the cache for the rest of the session.
    fontCache.delete(url);
    throw error;
  }

  doc.addFileToVFS(fileName, base64);

  return fileName;
}

/**
 * Fetches a binary asset and encodes it as base64.
 *
 * @param url - The asset URL.
 * @returns The asset as a base64 string.
 * @throws When the response is not ok.
 */
export async function fetchAsBase64(url: string): Promise<string> {

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to load export asset "${url}" (${response.status} ${response.statusText}).`);
  }

  const buffer = new Uint8Array(await response.arrayBuffer());
  let binary = '';

  // Chunked so a large asset cannot blow the argument limit of fromCharCode.
  const CHUNK = 0x8000;

  for (let index = 0; index < buffer.length; index += CHUNK) {
    binary += String.fromCharCode(...buffer.subarray(index, index + CHUNK));
  }

  return btoa(binary);
}
